import { InlineIcon } from "@iconify/react";
import { Form, Formik } from "formik";
import _ from "lodash";
import { DateTime } from "luxon";
import React, { useContext, useState } from "react";
import { createUseStyles } from "react-jss";
import { useSelector } from "react-redux";
import { GlobalModalsContext, NoScrollContext } from "../../context";
import jssVariables from "../../jss_setup";
import { modalGenericStyles } from "../../jss_setup/modals";
import {
  CurrencyContext,
  StyleTypeContext,
  TranslationContext,
} from "../../umbilical_tools";
import { SearchButton } from "../common/Button";
import { DatepickerField, ReactSelectField } from "../forms/inputGroup/helpers";
import { PaxPicker } from "../MainSearchForm/widgets";
import { PortAutoCompleteField } from "../forms/inputGroup/helpers/PortAutoComplete";
import { validateFerrySearch } from "../MainSearchForm/ferry/helpers";
import { getRoutesByOriginPort } from "../../api/ferries";
import { getIsRoundTripOpts } from "../MainSearchForm/ferry";
import PaxPickerModal from "../MainSearchForm/modals/PaxPickerModal";

function createPayload({ form }) {
  const payload = {
    adt: form.adults,
    chd: form.children,
    ages: form.children_ages,
    rTrp: form.isRoundtrip,
    orgn: form.origin,
    dest: form.destination,
    out: form.departDateTime,
    ...(form.returnDateTime ? { ret: form.returnDateTime } : {}),
  };
  return payload;
}

const styles = createUseStyles({
  ...modalGenericStyles,
  DeepFerrySearchForm: (props) => ({
    ...modalGenericStyles.modal,
    position: "fixed",
    height: "100vh",
    width: "100vw",
    zIndex: 9999,
    ..._.get(props, "customStyles.DeepFerrySearchForm", {}),
  }),
  searchModal: (props) => ({
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90vw",
    background: jssVariables.colors.background.light,
    padding: jssVariables.normal_gap,
    borderRadius: jssVariables.border_radius,
    display: "grid",
    gridGap: jssVariables.normal_gap,
    ..._.get(props, "customStyles.searchModal", {}),
  }),
  header: (props) => ({
    display: "grid",
    gridTemplateColumns: "repeat(2, max-content)",
    justifyContent: "space-between",
    fontSize: "large",
    fontWeight: "bold",
    color: jssVariables.colors.text.dark,
    ..._.get(props, "customStyles.header", {}),
  }),
  hotelSearchForm: (props) => ({
    display: "grid",
    gridTemplateRows: "repeat(4, max-content)",
    gridGap: jssVariables.normal_gap,
    padding: jssVariables.normal_gap,
    alignItems: "center",
    ..._.get(props, "customStyles.hotelSearchForm", {}),
  }),
  closeIcon: (props) => ({
    fontSize: "xx-large",
    cursor: "pointer",
    ..._.get(props, "customStyles.closeIcon", {}),
  }),
  searchBtnContr: (props) => ({
    width: "max-content",
    margin: "auto",
    ..._.get(props, "customStyles.searchBtnContr", {}),
  }),
  form: (props) => ({
    display: "grid",
    gridGap: jssVariables.normal_gap,
    ..._.get(props, "customStyles.form", {}),
  }),
  formContr: (props) => ({
    display: "grid",
    ..._.get(props, "customStyles.formContr", {}),
  }),
  row1: (props) => ({
    display: "grid",
    gridGap: jssVariables.half_gap,
    ..._.get(props, "customStyles.row1", {}),
  }),
  row2: (props) => ({
    display: "grid",
    gridGap: jssVariables.half_gap,
    ..._.get(props, "customStyles.row2", {}),
  }),
  mobileToggle: (props) => ({
    ..._.get(props, "customStyles.mobileToggle", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    searchModal: (props) => ({
      width: "70rem",
      ..._.get(props, "customStyles.searchModal.smallscreen", {}),
    }),
    hotelSearchForm: (props) => ({
      gridTemplateRows: "unset",
      gridTemplateColumns: "repeat(3, 1fr) max-content",
      alignItems: "end",
      gridGap: jssVariables.half_gap,
      ..._.get(
        props,
        "customStyles.hotelSearchForm.smallscreen.smallscreen",
        {}
      ),
    }),
    searchBtnContr: (props) => ({
      margin: "unset",
      paddingBottom: "1px",
      ..._.get(props, "customStyles.searchBtnContr.smallscreen", {}),
    }),
    form: (props) => ({
      gridTemplateColumns: "1fr max-content",
      gridGap: jssVariables.half_gap,
      alignItems: "end",
      ..._.get(props, "customStyles.form.smallscreen", {}),
    }),
    formContr: (props) => ({
      gridTemplateColumns: "1fr",
      gridGap: jssVariables.half_gap,
      ..._.get(props, "customStyles.formContr.smallscreen", {}),
    }),
    row1: (props) => ({
      gridTemplateColumns: "10rem 1fr 1fr",
      ..._.get(props, "customStyles.row1.smallscreen", {}),
    }),
    row2: (props) => ({
      gridTemplateColumns: props.roundTrip
        ? "10rem 1fr 1fr 1fr"
        : "10rem 1fr 1fr",
      ..._.get(props, "customStyles.row2.smallscreen", {}),
    }),
    mobileToggle: (props) => ({
      gridRow: "span 2",
      ..._.get(props, "customStyles.mobileToggle.smallscreen", {}),
    }),
  },
});
const DeepFerrySearchForm = () => {
  const [showPaxPickerModal, setShowPaxPickerModal] = useState(false);
  const [destPortsOptions, setDestPortsOptions] = useState([]);

  const search_form = useSelector((state) => state.FerrySearchForm);

  const [formPaxLocal, setFormPaxLocal] = useState({
    adults: search_form.adults,
    children: search_form.children,
    children_ages: search_form.children_ages,
  });
  const { t, i18n } = useContext(TranslationContext);
  const { currency } = useContext(CurrencyContext);
  const { type, brand, cStyler } = useContext(StyleTypeContext);
  const { setShowFerFormModal } = useContext(GlobalModalsContext);

  const [isRoundtrip, setIsRoundtrip] = useState(search_form.isRoundtrip);

  const customStyles = cStyler({
    type,
    brand,
    objName: "DeepFerrySearchForm",
  });

  const classes = styles({
    customStyles,
    roundTrip: isRoundtrip.value,
  });

  async function fetchDestPorts(originPort) {
    const response = await getRoutesByOriginPort(originPort);
    if (
      _.get(response, "routes.length", 0) &&
      response.routes[0].origin_port.code !==
        _.get(destPortsOptions, "0.origin_port.code")
    ) {
      setDestPortsOptions(response.routes);
    }
  }

  async function onNewSearch(form) {
    const encoded_data = window.btoa(JSON.stringify(createPayload({ form })));
    window.open(`/deep-ferry-search/${encoded_data}`, "_blank");
    setShowFerFormModal(false);
  }

  function onClickPax() {
    setShowPaxPickerModal(true);
  }

  return (
    <div className={classes.DeepFerrySearchForm}>
      <div className={classes.searchModal}>
        <div className={classes.header}>
          <span>{t("performNewSearch")}</span>
          <InlineIcon
            icon="ei:close"
            className={classes.closeIcon}
            onClick={() => setShowFerFormModal(false)}
          />
        </div>
        <Formik
          initialValues={{
            isRoundtrip: getIsRoundTripOpts(t)[0],
            origin: search_form.origin,
            destination: search_form.destination,
            departDateTime: [search_form.departDateTime],
            returnDateTime: [search_form.returnDateTime],
          }}
          validateOnBlur={false}
          validateOnMount={false}
          validate={(values) => {
            if (values.isRoundtrip !== isRoundtrip) {
              setIsRoundtrip(values.isRoundtrip);
            }
            if (_.get(values.origin, "origin_meta.0.code", "")) {
              fetchDestPorts(values.origin.origin_meta[0].code);
            }
          }}
          onSubmit={async (values) => {
            const destObj = destPortsOptions.find(
              (option) =>
                option.destination_port.code === values.destination.value
            );

            const hasErrors = validateFerrySearch({
              origin: _.get(values.origin, "origin_meta.0", {}),
              destination: _.get(values.destination, "value", {}),
              departDateTime: values.departDateTime[0],
              returnDateTime: values.returnDateTime[0],
              isRoundtrip: values.isRoundtrip.value,
            });
            if (hasErrors) {
              return;
            }
            var form = {
              origin: values.origin.origin_meta[0],
              destination: destObj.destination_port,
              isRoundtrip: values.isRoundtrip.value,
              departDateTime: values.departDateTime[0],
              ...(values.isRoundtrip.value
                ? { returnDateTime: values.returnDateTime[0] }
                : null),
              currency,
              language: i18n.language,
              adults: formPaxLocal.adults,
              children: formPaxLocal.children,
              children_ages: formPaxLocal.children_ages,
            };

            onNewSearch(form);
          }}>
          <Form className={classes.form}>
            <div className={classes.formContr}>
              <div className={classes.row1}>
                <span className={classes.mobileToggle}>
                  <ReactSelectField
                    name="isRoundtrip"
                    options={getIsRoundTripOpts(t)}
                    customStyles={_.get(customStyles, "inputGroupField", {})}
                  />
                </span>
                <PortAutoCompleteField
                  name="origin"
                  placeholder={t("selectOriginPort")}
                  withErrors={false}
                  customStyles={_.get(customStyles, "inputGroupField", {})}
                />
                <ReactSelectField
                  name="destination"
                  placeholder={t("selectDestPort")}
                  options={_.orderBy(destPortsOptions, "name").map((route) => ({
                    value: route.destination_port.code,
                    label: route.name,
                  }))}
                  customStyles={_.get(customStyles, "inputGroupField", {})}
                />
              </div>
              <div className={classes.row2}>
                <div />
                <DatepickerField
                  name="departDateTime"
                  dateFormat="MMM d, yyyy HH:mm"
                  minDate={DateTime.now().plus({ days: 1 }).toISODate()}
                  maxDate={DateTime.now().plus({ days: 365 }).toISODate()}
                  withPortal={true}
                  showTimeSelect={true}
                  customStyles={_.get(customStyles, "inputGroupField", {})}
                  extraStyles={{}}
                />
                {isRoundtrip.value && (
                  <DatepickerField
                    name="returnDateTime"
                    dateFormat="MMM d, yyyy HH:mm"
                    minDate={DateTime.now().plus({ days: 1 }).toISODate()}
                    maxDate={DateTime.now().plus({ days: 365 }).toISODate()}
                    withPortal={true}
                    showTimeSelect={true}
                    customStyles={_.get(customStyles, "inputGroupField", {})}
                    extraStyles={{}}
                  />
                )}
                <PaxPicker
                  customStyles={_.get(customStyles, "inputGroupField", {})}
                  adults={formPaxLocal.adults}
                  childs={formPaxLocal.children}
                  children_ages={formPaxLocal.children_ages}
                  onClick={onClickPax}
                  product_type="FER"
                  showLabel={false}
                />
              </div>
            </div>
            <div className={classes.searchBtnContr}>
              <SearchButton
                type="submit"
                text={t("search")}
                loading={false}
                customStyles={{
                  height: "3rem",
                  ..._.get(customStyles, "searchBtn", {}),
                }}
              />
            </div>
          </Form>
        </Formik>
      </div>
      {showPaxPickerModal && (
        <PaxPickerModal
          adults={formPaxLocal.adults}
          childs={formPaxLocal.children}
          children_ages={formPaxLocal.children_ages}
          onCancel={() => {
            setShowPaxPickerModal(false);
          }}
          onFormUpdate={({ form }) => {
            setFormPaxLocal({
              adults: form.adults,
              children: form.children,
              children_ages: form.children_ages,
            });
          }}
        />
      )}
    </div>
  );
};
DeepFerrySearchForm.propTypes = {};
export default DeepFerrySearchForm;
